/* ==========================================================================
   FREE GIFTS — AUTO-ADD TO CART
   File: assets/free-gifts.js

   Reads cart items' `product.metafields.custom.free_gifts` (via the inline
   <script id="FreeGiftsData"> rendered by snippets/cart-drawer.liquid).
   After any /cart/add|change|update, reconciles: adds missing gifts,
   removes orphan gifts, re-renders the cart drawer.
   ========================================================================== */

(function () {
  const PROPERTY_KEY = '_free_gift';
  const TRIGGER_KEY = '_gift_for';

  function readGiftConfig() {
    const node = document.getElementById('FreeGiftsData');
    if (!node) { console.log('[FreeGifts] No #FreeGiftsData node found'); return { gifts: [] }; }
    try {
      const cfg = JSON.parse(node.textContent || '{}');
      console.log('[FreeGifts] Config loaded:', cfg);
      return cfg;
    }
    catch (e) { console.warn('[FreeGifts] Bad JSON in #FreeGiftsData', e, node.textContent); return { gifts: [] }; }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function addItem(variantId, properties) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: properties || {} }] })
    }).then(r => r.json());
  }

  async function removeItem(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(r => r.json());
  }

  function computeExpectedGifts(cart, config) {
    const expected = new Map();
    cart.items.forEach((item) => {
      if (item.properties && item.properties[PROPERTY_KEY]) return;
      config.gifts.forEach((g) => {
        if (g.trigger_product_id === item.product_id || g.trigger_variant_id === item.variant_id) {
          if (!expected.has(g.gift_variant_id)) expected.set(g.gift_variant_id, item.product_id);
        }
      });
    });
    return expected;
  }

  function currentGifts(cart) {
    const map = new Map();
    cart.items.forEach((item) => {
      if (item.properties && item.properties[PROPERTY_KEY]) map.set(item.variant_id, item.key);
    });
    return map;
  }

  async function reconcileGifts() {
    const config = readGiftConfig();
    let cart;
    try { cart = await fetchCart(); } catch (e) { console.warn('[FreeGifts] cart fetch failed', e); return false; }

    const expected = computeExpectedGifts(cart, config);
    const present = currentGifts(cart);
    console.log('[FreeGifts] Reconciling. Expected:', Array.from(expected.keys()), 'Present:', Array.from(present.keys()));
    let changed = false;

    for (const [variantId, triggerProductId] of expected.entries()) {
      if (!present.has(variantId)) {
        try {
          console.log('[FreeGifts] Adding gift variant', variantId);
          await addItem(variantId, { [PROPERTY_KEY]: 'true', [TRIGGER_KEY]: String(triggerProductId) });
          changed = true;
        } catch (e) { console.warn('[FreeGifts] add failed', variantId, e); }
      }
    }
    for (const [variantId, key] of present.entries()) {
      if (!expected.has(variantId)) {
        try {
          console.log('[FreeGifts] Removing orphan gift', key);
          await removeItem(key);
          changed = true;
        }
        catch (e) { console.warn('[FreeGifts] remove failed', key, e); }
      }
    }
    return changed;
  }

  async function rerenderDrawer() {
    try {
      const res = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      const data = await res.json();
      const parser = new DOMParser();

      if (data['cart-drawer']) {
        const newDoc = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newDrawer = newDoc.querySelector('cart-drawer');
        const oldDrawer = document.querySelector('cart-drawer');
        if (newDrawer && oldDrawer) {
          const wasOpen = oldDrawer.classList.contains('active');
          oldDrawer.innerHTML = newDrawer.innerHTML;
          const overlay = oldDrawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', () => oldDrawer.close && oldDrawer.close());
          if (wasOpen) oldDrawer.classList.add('active');
        }
      }
      if (data['cart-icon-bubble']) {
        const newDoc = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
        const newBubble = newDoc.querySelector('#shopify-section-cart-icon-bubble') || newDoc.querySelector('.shopify-section');
        const oldBubble = document.querySelector('#shopify-section-cart-icon-bubble');
        if (newBubble && oldBubble) oldBubble.innerHTML = newBubble.innerHTML;
      }
    } catch (e) { console.warn('[FreeGifts] rerender failed', e); }
  }

  function installFetchHook() {
    const origFetch = window.fetch;
    let reconciling = false;

    window.fetch = async function (...args) {
      const response = await origFetch.apply(this, args);
      try {
        const url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
        const isCartMutation = /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
        if (isCartMutation && !reconciling) {
          const init = args[1] || {};
          const body = init.body || '';
          const isInternal = (typeof body === 'string' && body.includes('"_free_gift"'));
          if (!isInternal) {
            reconciling = true;
            console.log('[FreeGifts] Cart mutation detected:', url);
            setTimeout(async () => {
              try {
                // 1) Refresh drawer FIRST so gifts show instantly (rendered from
                //    metafield Liquid loop — no need to wait for /cart/add to finish).
                console.log('[FreeGifts] Refreshing drawer (gifts show optimistically)');
                await rerenderDrawer();
                // 2) Quietly add the real cart line items in the background so the
                //    gifts persist to checkout. No re-render needed afterwards
                //    since the gifts are already visible.
                console.log('[FreeGifts] Background-adding gift line items');
                await reconcileGifts();
              } catch (e) { console.warn('[FreeGifts] reconcile sequence failed', e); }
              reconciling = false;
            }, 50);
          }
        }
      } catch (e) { /* never break the original fetch */ }
      return response;
    };
  }

  async function init() {
    installFetchHook();
    try {
      // On page load, gifts already display optimistically via Liquid render.
      // Just sync the cart line items quietly in the background — no rerender.
      await reconcileGifts();
    } catch (e) { console.warn('[FreeGifts] init reconcile failed', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();